import React, { useState, Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider, useCart } from "./context/CartContext";
import { OptimizedImageProvider } from "./context/OptimizedImageContext";
import { RegionalProvider } from "./context/RegionalContext";
import { ShoppingCart, X } from "lucide-react";

// React App Entry Point

// Layout - Always loaded
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";

// Core Components - Always loaded
import ScrollToTop from "./components/ScrollToTop";
import MaintenanceGuard from "./components/MaintenanceGuard";
import AuthGate from "./components/AuthGate";
import AgeGate from "./components/AgeGate";

// Critical Public Pages - Eagerly loaded for fast initial render
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";

// Lazy-loaded Pages - Code split for better performance
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const CheckoutSuccessPage = lazy(() => import("./pages/CheckoutSuccessPage"));
const ExternalCheckoutPage = lazy(() => import("./pages/ExternalCheckoutPage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
// B2B Compliance: Research pages removed - no research chemicals or studies content
// const ResearchPage = lazy(() => import("./pages/ResearchPage"));
// const PeptideResearchPage = lazy(() => import("./pages/PeptideResearchPage"));
// B2B Compliance: Dosing calculator removed - no dosage/protocol content allowed
// const PeptideDosingCalculatorPage = lazy(() => import("./pages/PeptideDosingCalculatorPage"));
const COALibraryPage = lazy(() => import("./pages/COALibraryPage"));  // deprecated, kept for build safety only, no route
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));

// Research Portal Pages - Removed for B2B Compliance
// const ResearchPortalPage = lazy(() => import("./pages/ResearchPortalPage"));
// const PeptideProfilePage = lazy(() => import("./pages/PeptideProfilePage"));

// Legal & Policy Pages - Lazy loaded
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsConditionsPage = lazy(() => import("./pages/TermsConditionsPage"));
const ShippingPolicyPage = lazy(() => import("./pages/ShippingPolicyPage"));
const RefundsPolicyPage = lazy(() => import("./pages/RefundsPolicyPage"));
const AccessibilityPage = lazy(() => import("./pages/AccessibilityPage"));

// Account Pages - Lazy loaded
const AccountLayout = lazy(() => import("./pages/account/AccountLayout"));
const AccountDashboard = lazy(() => import("./pages/account/AccountDashboard"));
const AccountProfile = lazy(() => import("./pages/account/AccountProfile"));
const AccountAddresses = lazy(() => import("./pages/account/AccountAddresses"));
const AccountOrders = lazy(() => import("./pages/account/AccountOrders"));
const AccountWishlist = lazy(() => import("./pages/account/AccountWishlist"));

// B2B Account Status Pages - Lazy loaded
const AccountPendingPage = lazy(() => import("./pages/account/AccountPendingPage"));
const AccountRejectedPage = lazy(() => import("./pages/account/AccountRejectedPage"));

// Institutional Access Policy Page
const InstitutionalAccessPage = lazy(() => import("./pages/InstitutionalAccessPage"));

// Admin Pages - Lazy loaded (only admin users need these)
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminInventory = lazy(() => import("./pages/admin/AdminInventory"));
const AdminCOAs = lazy(() => import("./pages/admin/AdminCOAs"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminPromos = lazy(() => import("./pages/admin/AdminPromos"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminAbandonedCarts = lazy(() => import("./pages/admin/AdminAbandonedCarts"));
const AdminResearchCMS = lazy(() => import("./pages/admin/AdminResearchCMS"));  // B2B Compliance: disabled but kept for build
const AdminRegionalSettings = lazy(() => import("./pages/admin/AdminRegionalSettings"));
const AdminFulfillmentSettings = lazy(() => import("./pages/admin/AdminFulfillmentSettings"));
const AdminFulfillmentOrders = lazy(() => import("./pages/admin/AdminFulfillmentOrders"));
const AdminFulfillmentLogs = lazy(() => import("./pages/admin/AdminFulfillmentLogs"));
const AdminTrackingSettings = lazy(() => import("./pages/admin/AdminTrackingSettings"));
const AdminTrackingLogs = lazy(() => import("./pages/admin/AdminTrackingLogs"));
const AdminCopilot = lazy(() => import("./pages/admin/AdminCopilot"));
const AdminUserVerification = lazy(() => import("./pages/admin/AdminUserVerification"));
const AdminPaymentSettings = lazy(() => import("./pages/admin/AdminPaymentSettings"));
const AdminAccountReview = lazy(() => import("./pages/admin/AdminAccountReview"));
// B2B Compliance: Research CMS removed
// const AdminResearchCMS = lazy(() => import("./pages/admin/AdminResearchCMS"));

// Loading Spinner Component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
      <p className="text-sm text-slate-500">Loading...</p>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// B2B Protected Layout - Requires authentication to access store
const B2BProtectedLayout = ({ children }) => {
  return (
    <AuthGate>
      <Header />
      <main>{children}</main>
      <Footer />
    </AuthGate>
  );
};

// Immersive Layout for Homepage (no header padding, integrated header, footer at end)
const ImmersiveLayout = ({ children }) => {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
};

// Public Layout with Header and Footer - Content extends behind header
const PublicLayout = ({ children }) => {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
};

// Auth Layout (no header/footer)
const AuthLayout = ({ children }) => {
  return <main className="min-h-screen">{children}</main>;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Homepage - Scene-based Experience */}
        <Route
          path="/"
          element={
            <ImmersiveLayout>
              <HomePage />
            </ImmersiveLayout>
          }
        />
        
        {/* Public Routes - B2B Protected (require login to view store) */}
        <Route
          path="/products"
          element={
            <B2BProtectedLayout>
              <ProductsPage />
            </B2BProtectedLayout>
          }
        />
        <Route
          path="/products/:slug"
          element={
            <B2BProtectedLayout>
              <ProductDetailPage />
            </B2BProtectedLayout>
          }
        />
        {/* B2B Compliance: Research routes removed - no research chemicals content */}
        
        <Route
          path="/contact"
          element={
            <PublicLayout>
              <ContactPage />
            </PublicLayout>
          }
        />
        <Route
          path="/about"
          element={
            <PublicLayout>
              <AboutPage />
            </PublicLayout>
          }
        />
        
        {/* Auth Routes */}
        <Route
          path="/login"
          element={
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          }
        />
        <Route
          path="/register"
          element={
            <AuthLayout>
              <RegisterPage />
            </AuthLayout>
          }
        />
        
        {/* Checkout Routes - B2B Protected */}
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <B2BProtectedLayout>
                <CheckoutPage />
              </B2BProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout/success"
          element={
            <B2BProtectedLayout>
              <CheckoutSuccessPage />
            </B2BProtectedLayout>
          }
        />
        {/* External Checkout - For partner sites (iframe/modal) */}
        <Route
          path="/checkout/external"
          element={<ExternalCheckoutPage />}
        />
        {/* Account Routes - Full Dashboard */}
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AccountLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AccountDashboard />} />
          <Route path="profile" element={<AccountProfile />} />
          <Route path="addresses" element={<AccountAddresses />} />
          <Route path="orders" element={<AccountOrders />} />
          <Route path="orders/:orderId" element={<AccountOrders />} />
          <Route path="wishlist" element={<AccountWishlist />} />
        </Route>
        
        {/* Legacy Orders Page - Redirect to Account */}
        <Route
          path="/orders"
          element={<Navigate to="/account/orders" replace />}
        />
        
        {/* Legal & Policy Pages */}
        <Route path="/privacy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
        <Route path="/terms" element={<PublicLayout><TermsConditionsPage /></PublicLayout>} />
        <Route path="/shipping" element={<PublicLayout><ShippingPolicyPage /></PublicLayout>} />
        <Route path="/refunds" element={<PublicLayout><RefundsPolicyPage /></PublicLayout>} />
        <Route path="/accessibility" element={<PublicLayout><AccessibilityPage /></PublicLayout>} />
        
        {/* Institutional Access Policy - Public page explaining B2B requirements */}
        <Route path="/institutional-access" element={<InstitutionalAccessPage />} />
        
        {/* B2B Account Status Pages */}
        <Route
          path="/account/pending"
          element={
            <AuthLayout>
              <AccountPendingPage />
            </AuthLayout>
          }
        />
        <Route
          path="/account/rejected"
          element={
            <AuthLayout>
              <AccountRejectedPage />
            </AuthLayout>
          }
        />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="inventory" element={<AdminInventory />} />
          <Route path="coas" element={<AdminCOAs />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="user-verification" element={<AdminUserVerification />} />
          <Route path="accounts" element={<AdminAccountReview />} />
          <Route path="promos" element={<AdminPromos />} />
          <Route path="abandoned-carts" element={<AdminAbandonedCarts />} />
          {/* B2B Compliance: Research CMS removed */}
          <Route path="regional" element={<AdminRegionalSettings />} />
          <Route path="fulfillment" element={<AdminFulfillmentSettings />} />
          <Route path="fulfillment/orders" element={<AdminFulfillmentOrders />} />
          <Route path="fulfillment/logs" element={<AdminFulfillmentLogs />} />
          <Route path="tracking" element={<AdminTrackingSettings />} />
          <Route path="tracking/logs" element={<AdminTrackingLogs />} />
          <Route path="copilot" element={<AdminCopilot />} />
          <Route path="payments" element={<AdminPaymentSettings />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>
        
        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AgeGate>
          <MaintenanceGuard>
            <AuthProvider>
              <CartProvider>
                <OptimizedImageProvider>
                  <RegionalProvider>
                    <AppRoutes />
                    
                    {/* Product Quick View Modal */}
                    {selectedProduct && (
                      <ProductQuickViewModal 
                        product={selectedProduct} 
                        onClose={() => setSelectedProduct(null)} 
                      />
                    )}
                    
                    <Toaster 
                      position="top-right" 
                      richColors 
                      closeButton
                      toastOptions={{
                        className: 'font-body',
                      }}
                    />
                  </RegionalProvider>
                </OptimizedImageProvider>
              </CartProvider>
            </AuthProvider>
          </MaintenanceGuard>
        </AgeGate>
      </BrowserRouter>
    </HelmetProvider>
  );
}

// Product Quick View Modal (simplified version for drawer)
const ProductQuickViewModal = ({ product, onClose }) => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  
  const handleAddToCart = async () => {
    try {
      await addToCart(product._id || product.id, 1);
      toast.success(`${product.name} added to cart`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4 text-slate-600" />
        </button>
        
        {/* Product Image */}
        <div className="bg-gradient-to-br from-slate-50 to-white p-8 flex items-center justify-center h-64">
          <img
            src={product.image_url}
            alt={product.name}
            className="max-w-full max-h-full object-contain"
          />
        </div>
        
        {/* Product Info */}
        <div className="p-6">
          <p className="text-xs font-medium text-blue-600 uppercase tracking-wider mb-1">
            Research Peptide
          </p>
          <h3 className="font-heading font-semibold text-xl text-slate-900 mb-1">
            {product.name}
          </h3>
          <p className="text-sm text-slate-500 mb-2">{product.dosage}</p>
          <p className="text-2xl font-bold text-blue-600 mb-4">
            ${product.price?.toFixed(2)}
          </p>
          
          <div className="flex gap-3">
            <button
              onClick={handleAddToCart}
              className="flex-1 py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
            >
              <ShoppingCart className="w-4 h-4" />
              Add to Cart
            </button>
            <button
              onClick={() => {
                onClose();
                navigate(`/products/${product._id || product.id}`);
              }}
              className="px-4 py-3 border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-colors"
            >
              Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
